import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Download,
  RefreshCw,
  ArrowLeft,
  Clock,
  Book,
  Star,
  Edit,
  Calendar as CalendarIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { submitReview } from '@/hooks/useReviewSubmit';
import Papa from 'papaparse';
import { QuickReviewDialog } from '@/components/QuickReviewDialog';
import { EditBookDatesDialog } from '@/components/EditBookDatesDialog';
import { CoverImage } from '@/components/CoverImage';
import { ShelfMappingWizard } from '@/components/import/ShelfMappingWizard';

interface ImportJob {
  id: string;
  status: 'pending' | 'processing' | 'complete' | 'error';
  source: string;
  file_path?: string;
  row_count?: number;
  processed_count?: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

interface ImportRow {
  id: string;
  import_job_id: string;
  raw_data: any;
  mapped_book_id?: string;
  mapped_status: 'ok' | 'skipped' | 'error';
  message?: string;
}

interface CSVPreview {
  headers: string[];
  rows: any[];
  totalRows: number;
}

interface ImportedBook {
  id: string;
  book_id: string;
  rating_star?: number;
  review_text?: string;
  started_at?: string;
  finished_at?: string;
  read_at?: string;
  book: {
    id: string;
    title: string;
    authors: string[];
    cover_url?: string;
    cover_cache_url?: string;
    isbn13?: string;
    isbn10?: string;
    open_library_id?: string;
  };
}

type ImportStep = 'upload' | 'mapping' | 'processing' | 'complete';

export default function Import() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CSVPreview | null>(null);
  const [currentJob, setCurrentJob] = useState<ImportJob | null>(null);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importedBooks, setImportedBooks] = useState<ImportedBook[]>([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [step, setStep] = useState<ImportStep>('upload');
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  
  // Dialog states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [datesDialogOpen, setDatesDialogOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState<ImportedBook | null>(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check for existing imports on load
  useEffect(() => {
    if (user) {
      loadRecentImport();
    }
  }, [user]);

  // Poll for job status updates
  useEffect(() => {
    if (currentJob && (currentJob.status === 'pending' || currentJob.status === 'processing')) {
      const interval = setInterval(checkJobStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [currentJob]);

  const loadRecentImport = async () => {
    try {
      const { data: job } = await supabase
        .from('import_jobs')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (job) {
        setCurrentJob(job as ImportJob);
        if (job.status === 'complete' || job.status === 'error') {
          loadImportRows(job.id);
        }
      }
    } catch (error) {
      // No recent import found, which is fine
    }
  };

  const checkJobStatus = async () => {
    if (!currentJob) return;

    try {
      const { data: job } = await supabase
        .from('import_jobs')
        .select('*')
        .eq('id', currentJob.id)
        .single();

      if (job && job.status !== currentJob.status) {
        setCurrentJob(job as ImportJob);
        
        if (job.status === 'complete') {
          loadImportRows(job.id);
          loadImportedBooks(job.id);
          toast({
            title: "Import complete!",
            description: `Successfully processed ${job.processed_count} of ${job.row_count} rows.`,
          });
        } else if (job.status === 'error') {
          toast({
            variant: "destructive",
            title: "Import failed",
            description: job.error_message || "There was an error processing your file.",
          });
        }
      }
    } catch (error) {
      console.error('Error checking job status:', error);
    }
  };

  const loadImportRows = async (jobId: string) => {
    try {
      const { data: rows } = await supabase
        .from('import_rows')
        .select('*')
        .eq('import_job_id', jobId)
        .order('created_at', { ascending: true });

      if (rows) {
        setImportRows(rows as ImportRow[]);
      }
    } catch (error) {
      console.error('Error loading import rows:', error);
    }
  };

  const loadImportedBooks = async (jobId: string) => {
    try {
      setLoadingBooks(true);
      
      // Get successfully imported book IDs from import_rows
      const { data: rows } = await supabase
        .from('import_rows')
        .select('mapped_book_id')
        .eq('import_job_id', jobId)
        .eq('mapped_status', 'ok')
        .not('mapped_book_id', 'is', null);

      if (!rows || rows.length === 0) return;

      const bookIds = rows.map(r => r.mapped_book_id).filter(Boolean);

      // Fetch user_books with book details
      const { data: userBooks } = await supabase
        .from('user_books')
        .select(`
          id,
          book_id,
          rating_star,
          review_text,
          started_at,
          finished_at,
          read_at,
          books!user_books_book_id_fkey (
            id,
            title,
            authors,
            cover_url,
            cover_cache_url,
            isbn13,
            isbn10,
            open_library_id
          )
        `)
        .eq('user_id', user?.id)
        .in('book_id', bookIds)
        .order('created_at', { ascending: false });

      if (userBooks) {
        // Transform the data to match our interface
        const transformedBooks = userBooks.map((ub: any) => ({
          id: ub.id,
          book_id: ub.book_id,
          rating_star: ub.rating_star,
          review_text: ub.review_text,
          started_at: ub.started_at,
          finished_at: ub.finished_at,
          read_at: ub.read_at,
          book: ub.books,
        }));
        setImportedBooks(transformedBooks as ImportedBook[]);
      }
    } catch (error) {
      console.error('Error loading imported books:', error);
    } finally {
      setLoadingBooks(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please select a CSV file.",
      });
      return;
    }

    setFile(selectedFile);
    previewCSV(selectedFile);
  };

  const previewCSV = (file: File) => {
    Papa.parse(file, {
      header: true,
      preview: 50, // Preview first 50 rows
      complete: (results) => {
        if (results.errors.length > 0) {
          toast({
            variant: "destructive",
            title: "CSV parsing error",
            description: "There was an error reading your CSV file.",
          });
          return;
        }

        const headers = results.meta.fields || [];
        const rows = results.data;
        
        // Estimate total rows by file size (rough approximation)
        const avgRowSize = file.size / results.data.length;
        const estimatedTotalRows = Math.floor(file.size / avgRowSize);

        setPreview({
          headers,
          rows: rows.slice(0, 10), // Show first 10 rows in preview
          totalRows: estimatedTotalRows,
        });
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        toast({
          variant: "destructive",
          title: "CSV parsing error",
          description: "Could not parse the CSV file.",
        });
      }
    });
  };

  const handleUploadForMapping = async () => {
    if (!file || !user) return;

    try {
      setUploading(true);

      // Upload file to Supabase Storage
      const fileName = `${user.id}/${Date.now()}-${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('imports')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Create import job in pending state (won't process yet)
      const { data: job, error: jobError } = await supabase
        .from('import_jobs')
        .insert({
          user_id: user.id,
          source: 'goodreads',
          file_path: uploadData.path,
          status: 'pending',
          row_count: preview?.totalRows || 0,
        })
        .select()
        .single();

      if (jobError) throw jobError;

      // Move to mapping step
      setPendingJobId(job.id);
      setStep('mapping');

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "Could not upload your file.",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleMappingComplete = async () => {
    if (!pendingJobId) return;

    try {
      // Trigger processing via Edge Function
      const { error: processError } = await supabase.functions.invoke('process-goodreads-import', {
        body: { importJobId: pendingJobId }
      });

      if (processError) {
        await supabase
          .from('import_jobs')
          .update({ 
            status: 'error', 
            error_message: 'Failed to start processing' 
          })
          .eq('id', pendingJobId);
        
        throw processError;
      }

      // Load the job and move to processing step
      const { data: job } = await supabase
        .from('import_jobs')
        .select('*')
        .eq('id', pendingJobId)
        .single();

      if (job) {
        setCurrentJob(job as ImportJob);
      }

      setStep('processing');
      setFile(null);
      setPreview(null);

      toast({
        title: "Import started",
        description: "Your file is being processed. This may take a few minutes.",
      });

    } catch (error) {
      console.error('Import error:', error);
      toast({
        variant: "destructive",
        title: "Import failed",
        description: "Could not start the import process.",
      });
    }
  };

  const handleUploadAndProcess = async () => {
    if (!file || !user) return;

    try {
      setUploading(true);

      // Upload file to Supabase Storage
      const fileName = `${user.id}/${Date.now()}-${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('imports')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Create import job
      const { data: job, error: jobError } = await supabase
        .from('import_jobs')
        .insert({
          user_id: user.id,
          source: 'goodreads',
          file_path: uploadData.path,
          status: 'pending',
          row_count: preview?.totalRows || 0,
        })
        .select()
        .single();

      if (jobError) throw jobError;

      setCurrentJob(job as ImportJob);

      // Trigger processing via Edge Function
      const { error: processError } = await supabase.functions.invoke('process-goodreads-import', {
        body: { importJobId: job.id }
      });

      if (processError) {
        // Update job status to error
        await supabase
          .from('import_jobs')
          .update({ 
            status: 'error', 
            error_message: 'Failed to start processing' 
          })
          .eq('id', job.id);
        
        throw processError;
      }

      // Clear file selection
      setFile(null);
      setPreview(null);
      setStep('processing');

      toast({
        title: "Import started",
        description: "Your file is being processed. This may take a few minutes.",
      });

    } catch (error) {
      console.error('Import error:', error);
      toast({
        variant: "destructive",
        title: "Import failed",
        description: "Could not start the import process.",
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorReport = async () => {
    if (!currentJob) return;

    try {
      const errorRows = importRows.filter(row => row.mapped_status === 'error');
      
      if (errorRows.length === 0) {
        toast({
          title: "No errors found",
          description: "All rows were processed successfully.",
        });
        return;
      }

      const csvData = errorRows.map(row => ({
        ...row.raw_data,
        error_message: row.message || 'Unknown error',
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `import-errors-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not generate error report.",
      });
    }
  };

  const startNewImport = () => {
    setCurrentJob(null);
    setImportRows([]);
    setFile(null);
    setPreview(null);
    setImportedBooks([]);
    setPendingJobId(null);
    setStep('upload');
  };

  const handleOpenReviewDialog = (book: ImportedBook) => {
    setSelectedBook(book);
    setReviewDialogOpen(true);
  };

  const handleOpenDatesDialog = (book: ImportedBook) => {
    setSelectedBook(book);
    setDatesDialogOpen(true);
  };

  const handleSubmitReview = async (data: {
    rating: number;
    reviewText: string;
    finishedAt?: Date;
    startedAt?: Date;
    privacy: 'public' | 'private';
    tags?: string[];
  }) => {
    if (!selectedBook || !selectedBook.book || !user) return;

    try {
      // Use centralized review submit - creates activity automatically
      const result = await submitReview(
        {
          rating: data.rating,
          reviewText: data.reviewText,
          finishedAt: data.finishedAt,
          startedAt: data.startedAt,
          tags: data.tags,
        },
        {
          userId: user.id,
          bookId: selectedBook.book.id,
          bookTitle: selectedBook.book.title,
          bookCoverUrl: selectedBook.book.cover_url,
          shelf: 'read',
          privacy: data.privacy,
        }
      );

      if (!result.success) {
        throw result.error || new Error('Failed to save review');
      }

      // Refresh imported books
      if (currentJob) {
        await loadImportedBooks(currentJob.id);
      }

      toast({
        title: "Review saved",
        description: "Your review has been added successfully.",
      });
    } catch (error) {
      console.error('Error saving review:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not save your review.",
      });
      throw error;
    }
  };

  const handleSubmitDates = async (data: {
    startedAt?: Date;
    finishedAt?: Date;
    readAt?: Date;
  }) => {
    if (!selectedBook) return;

    try {
      const updateData: any = {};

      if (data.startedAt) {
        updateData.started_at = data.startedAt.toISOString().split('T')[0];
      }
      if (data.finishedAt) {
        updateData.finished_at = data.finishedAt.toISOString().split('T')[0];
      }
      if (data.readAt) {
        updateData.read_at = data.readAt.toISOString().split('T')[0];
      }

      const { error } = await supabase
        .from('user_books')
        .update(updateData)
        .eq('id', selectedBook.id);

      if (error) throw error;

      // Refresh imported books
      if (currentJob) {
        await loadImportedBooks(currentJob.id);
      }

      toast({
        title: "Dates updated",
        description: "Reading dates have been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating dates:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update dates.",
      });
      throw error;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold mb-4">Authentication Required</h2>
            <p className="text-muted-foreground mb-4">Please sign in to import your reading data.</p>
            <Button onClick={() => navigate('/auth')}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-success" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-destructive" />;
      case 'processing':
        return <RefreshCw className="w-5 h-5 text-primary animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getProgressPercentage = () => {
    if (!currentJob || !currentJob.row_count) return 0;
    return Math.round(((currentJob.processed_count || 0) / currentJob.row_count) * 100);
  };

  return (
    <div className="min-h-screen bg-background">

      <main className="container mx-auto px-4 py-8 max-w-4xl space-y-8">
        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Book className="w-5 h-5" />
              Import from Goodreads
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Upload your Goodreads library export to automatically import your books, ratings, and shelves.
            </p>
            
            <Alert>
              <FileText className="w-4 h-4" />
              <AlertDescription>
                <strong>How to export from Goodreads:</strong>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>Go to Goodreads.com and sign in to your account</li>
                  <li>Navigate to "My Books" → "Import and export"</li>
                  <li>Click "Export Library" to download your CSV file</li>
                  <li>Upload the downloaded CSV file here</li>
                </ol>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Current Import Status */}
        {currentJob && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                {getStatusIcon(currentJob.status)}
                Import Status
              </CardTitle>
              <Badge variant={currentJob.status === 'complete' ? 'default' : 
                            currentJob.status === 'error' ? 'destructive' : 'secondary'}>
                {currentJob.status}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress</span>
                  <span>{currentJob.processed_count || 0} of {currentJob.row_count || 0} rows</span>
                </div>
                <Progress value={getProgressPercentage()} className="w-full" />
              </div>

              {currentJob.status === 'error' && currentJob.error_message && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>{currentJob.error_message}</AlertDescription>
                </Alert>
              )}

              {currentJob.status === 'complete' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-success">
                      {importRows.filter(r => r.mapped_status === 'ok').length}
                    </div>
                    <div className="text-sm text-muted-foreground">Imported</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-warning">
                      {importRows.filter(r => r.mapped_status === 'skipped').length}
                    </div>
                    <div className="text-sm text-muted-foreground">Skipped</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-destructive">
                      {importRows.filter(r => r.mapped_status === 'error').length}
                    </div>
                    <div className="text-sm text-muted-foreground">Errors</div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                {currentJob.status === 'complete' && importRows.some(r => r.mapped_status === 'error') && (
                  <Button variant="outline" onClick={downloadErrorReport}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Error Report
                  </Button>
                )}
                
                {(currentJob.status === 'complete' || currentJob.status === 'error') && (
                  <Button onClick={startNewImport}>
                    <Upload className="w-4 h-4 mr-2" />
                    Start New Import
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review Your Imported Books */}
        {currentJob?.status === 'complete' && importedBooks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Review Your Imported Books</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingBooks ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-4">
                  {importedBooks.map((userBook) => (
                    <div
                      key={userBook.id}
                      className="flex gap-4 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      {/* Book Cover */}
                      <div className="flex-shrink-0">
                        <CoverImage
                          bookId={userBook.book.id}
                          isbn13={userBook.book.isbn13}
                          isbn10={userBook.book.isbn10}
                          olid={userBook.book.open_library_id}
                          title={userBook.book.title}
                          author={userBook.book.authors?.[0]}
                          coverUrl={userBook.book.cover_url}
                          coverCacheUrl={userBook.book.cover_cache_url}
                          size="sm"
                          className="w-16"
                        />
                      </div>

                      {/* Book Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold line-clamp-2 mb-1">
                          {userBook.book.title}
                        </h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          by {userBook.book.authors?.join(', ') || 'Unknown Author'}
                        </p>

                        {/* Current Data */}
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-3">
                          {userBook.rating_star && (
                            <Badge variant="secondary" className="gap-1">
                              <Star className="w-3 h-3" />
                              {userBook.rating_star} stars
                            </Badge>
                          )}
                          {userBook.review_text && (
                            <Badge variant="secondary">Has review</Badge>
                          )}
                          {userBook.finished_at && (
                            <Badge variant="secondary">
                              Finished {new Date(userBook.finished_at).toLocaleDateString()}
                            </Badge>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenReviewDialog(userBook)}
                          >
                            <Star className="w-3 h-3 mr-1" />
                            {userBook.rating_star || userBook.review_text ? 'Edit Review' : 'Add Review'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenDatesDialog(userBook)}
                          >
                            <CalendarIcon className="w-3 h-3 mr-1" />
                            Edit Dates
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step: Shelf Mapping Wizard */}
        {step === 'mapping' && pendingJobId && user && (
          <ShelfMappingWizard
            importJobId={pendingJobId}
            userId={user.id}
            onComplete={handleMappingComplete}
            onBack={() => {
              setStep('upload');
              setPendingJobId(null);
            }}
          />
        )}

        {/* Step: File Upload */}
        {step === 'upload' && !currentJob && (
          <Card>
            <CardHeader>
              <CardTitle>Upload CSV File</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">
                    {file ? file.name : 'Choose CSV file'}
                  </p>
                  <p className="text-muted-foreground">
                    Click to browse or drag and drop your Goodreads export
                  </p>
                </label>
              </div>

              {file && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="w-4 h-4" />
                  <span>File size: {(file.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CSV Preview */}
        {preview && step === 'upload' && !currentJob && (
          <Card>
            <CardHeader>
              <CardTitle>File Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Showing first 10 rows of ~{preview.totalRows} total rows
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      {preview.headers.slice(0, 6).map((header, index) => (
                        <th key={index} className="px-3 py-2 text-left font-medium">
                          {header}
                        </th>
                      ))}
                      {preview.headers.length > 6 && (
                        <th className="px-3 py-2 text-left font-medium">
                          +{preview.headers.length - 6} more...
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, index) => (
                      <tr key={index} className="border-b border-border/50">
                        {preview.headers.slice(0, 6).map((header, cellIndex) => (
                          <td key={cellIndex} className="px-3 py-2 truncate max-w-32">
                            {row[header] || '—'}
                          </td>
                        ))}
                        {preview.headers.length > 6 && (
                          <td className="px-3 py-2 text-muted-foreground">...</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={handleUploadForMapping}
                  disabled={uploading}
                  className="flex-1"
                >
                  {uploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Continue to Shelf Mapping
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => { setFile(null); setPreview(null); }}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Dialogs */}
      {selectedBook && (
        <>
          <QuickReviewDialog
            isOpen={reviewDialogOpen}
            onClose={() => {
              setReviewDialogOpen(false);
              setSelectedBook(null);
            }}
            book={{
              id: selectedBook.book.id,
              title: selectedBook.book.title,
              author: selectedBook.book.authors?.[0] || 'Unknown Author',
            }}
            initialData={{
              rating: selectedBook.rating_star,
              reviewText: selectedBook.review_text,
              finishedAt: selectedBook.finished_at,
              startedAt: selectedBook.started_at,
              tags: [],
            }}
            onSubmit={handleSubmitReview}
          />
          
          <EditBookDatesDialog
            isOpen={datesDialogOpen}
            onClose={() => {
              setDatesDialogOpen(false);
              setSelectedBook(null);
            }}
            book={{
              id: selectedBook.book.id,
              title: selectedBook.book.title,
              author: selectedBook.book.authors?.[0] || 'Unknown Author',
            }}
            initialData={{
              startedAt: selectedBook.started_at,
              finishedAt: selectedBook.finished_at,
            }}
            onSubmit={handleSubmitDates}
          />
        </>
      )}
    </div>
  );
}